import React, { useEffect, useMemo, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import LoadingDNA from '../../components/LoadingDNA';
import { EmptyState, KpiCard, MetaStrip, SectionCard, StatusBadge, TableControls } from '../../components/admin/AdminPrimitives';
import { formatCurrencyINRFull, formatDateIN } from '../../utils/adminFormat';
import { AdminRecord, EmployeeDirectoryRecord, getAdmins, getCompanyEmployees, getTrips, isApiError } from '../../services/dashboardService';
import {
  ApprovalContinuityConfigRecord,
  ApprovalDelegationRecord,
  NotificationPreferenceRecord,
  ApprovalRuleRecord,
  createApprovalDelegation,
  createApprovalRule,
  getApprovalContinuityConfig,
  getExpenseEvents,
  getApprovalDelegations,
  getOpsAlerts,
  getOpsMetrics,
  getMyNotificationPreferences,
  getReconciliationExceptionsReport,
  getReconciliationReport,
  getTrustConfig,
  getApprovalRules,
  isExpenseApiError,
  ReconciliationExceptionRow,
  runEscalationScan,
  TrustConfigRecord,
  updateApprovalDelegation,
  updateApprovalContinuityConfig,
  updateMyNotificationPreference,
  updateApprovalRule,
  updateTrustConfig,
} from '../../services/expenseService';

const AdminSettingsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [admins, setAdmins] = useState<AdminRecord[]>([]);
  const [totalTrips, setTotalTrips] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [rules, setRules] = useState<ApprovalRuleRecord[]>([]);
  const [ruleSearch, setRuleSearch] = useState('');
  const [ruleStatusFilter, setRuleStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [alerts, setAlerts] = useState<Array<{ code: string; severity: string; message: string }>>([]);
  const [alertSearch, setAlertSearch] = useState('');
  const [adminSearch, setAdminSearch] = useState('');
  const [adminStatusFilter, setAdminStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [metrics, setMetrics] = useState<{ requests_total: number; latency_ms: { avg: number } } | null>(null);
  const [reconciliation, setReconciliation] = useState<{ parity_ok: boolean; delta: number } | null>(null);
  const [reconciliationExceptions, setReconciliationExceptions] = useState<ReconciliationExceptionRow[]>([]);
  const [employees, setEmployees] = useState<EmployeeDirectoryRecord[]>([]);
  const [delegations, setDelegations] = useState<ApprovalDelegationRecord[]>([]);
  const [delegationSearch, setDelegationSearch] = useState('');
  const [delegationStatusFilter, setDelegationStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [trustConfig, setTrustConfig] = useState<TrustConfigRecord>({
    duplicate_window_hours: 24,
    odd_hour_start: 23,
    odd_hour_end: 6,
    high_deviation_multiplier: 2.5,
    high_deviation_min_delta: 2000,
    baseline_lookback_days: 60,
    minimum_history_samples: 5,
  });
  const [continuityConfig, setContinuityConfig] = useState<ApprovalContinuityConfigRecord>({
    backup_approver_id: null,
    auto_escalation_hours: 24,
  });
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferenceRecord[]>([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [newRule, setNewRule] = useState({
    department: '',
    category: '',
    threshold_amount: 0,
    requires_finance: false,
    sla_hours: 24,
  });
  const [newDelegation, setNewDelegation] = useState({
    approver_id: '',
    delegate_approver_id: '',
    start_at: '',
    end_at: '',
    reason: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);

        const [adminRows, trips, events, approvalRules, opsAlerts, opsMetrics, recon, reconExceptions, employeeRows, delegationRows, trustConfigRow, continuityConfigRow, preferenceRows] = await Promise.all([
          getAdmins(),
          getTrips({ perPage: 500 }),
          getExpenseEvents({ perPage: 1000 }),
          getApprovalRules(),
          getOpsAlerts(),
          getOpsMetrics(),
          getReconciliationReport(),
          getReconciliationExceptionsReport({ perPage: 500 }),
          getCompanyEmployees(),
          getApprovalDelegations({ includeInactive: true }),
          getTrustConfig(),
          getApprovalContinuityConfig(),
          getMyNotificationPreferences(),
        ]);
        setAdmins(adminRows);
        setTotalTrips(trips.length);
        setTotalExpenses(events.length);
        setRules(approvalRules);
        setEmployees(employeeRows);
        setDelegations(delegationRows);
        setAlerts(opsAlerts);
        setTrustConfig(trustConfigRow);
        setContinuityConfig(continuityConfigRow);
        setNotificationPreferences(preferenceRows);
        setMetrics({ requests_total: opsMetrics.requests_total, latency_ms: { avg: opsMetrics.latency_ms.avg } });
        setReconciliation({ parity_ok: recon.parity_ok, delta: recon.delta });
        setReconciliationExceptions(reconExceptions);
        setLastRefreshedAt(new Date());
      } catch (error) {
        if (isExpenseApiError(error)) setErrorMessage(error.message);
        else if (isApiError(error)) setErrorMessage(error.message);
        else if (error instanceof Error) setErrorMessage(error.message);
        else setErrorMessage('Failed to load settings data.');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  const activeAdmins = useMemo(() => admins.filter((row) => row.is_active).length, [admins]);
  const filteredRules = useMemo(() => {
    const q = ruleSearch.trim().toLowerCase();
    return rules.filter((rule) => {
      if (ruleStatusFilter === 'active' && !rule.is_active) return false;
      if (ruleStatusFilter === 'inactive' && rule.is_active) return false;
      if (!q) return true;
      const haystack = [
        rule.id,
        rule.department || '',
        rule.category || '',
        rule.owner_name || '',
        String(rule.threshold_amount || ''),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [ruleSearch, ruleStatusFilter, rules]);

  const filteredAlerts = useMemo(() => {
    const q = alertSearch.trim().toLowerCase();
    if (!q) return alerts;
    return alerts.filter((alert) => `${alert.code} ${alert.severity} ${alert.message}`.toLowerCase().includes(q));
  }, [alertSearch, alerts]);

  const filteredAdmins = useMemo(() => {
    const q = adminSearch.trim().toLowerCase();
    return admins.filter((admin) => {
      if (adminStatusFilter === 'active' && !admin.is_active) return false;
      if (adminStatusFilter === 'inactive' && admin.is_active) return false;
      if (!q) return true;
      const haystack = [admin.id, admin.email, admin.company_id, admin.user_id].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [admins, adminSearch, adminStatusFilter]);

  const filteredDelegations = useMemo(() => {
    const q = delegationSearch.trim().toLowerCase();
    return delegations.filter((row) => {
      if (delegationStatusFilter === 'active' && !row.is_active) return false;
      if (delegationStatusFilter === 'inactive' && row.is_active) return false;
      if (!q) return true;
      const haystack = [
        row.id,
        row.approver_name || '',
        row.delegate_approver_name || '',
        row.reason || '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [delegationSearch, delegationStatusFilter, delegations]);

  const exportCsv = (filename: string, headers: string[], rows: Array<Array<string | number | boolean | null | undefined>>) => {
    const escapeCell = (value: string | number | boolean | null | undefined): string => {
      const text = String(value ?? '');
      if (text.includes(',') || text.includes('"') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };
    const csv = [headers.join(','), ...rows.map((row) => row.map(escapeCell).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const onExportReconciliationExceptions = () => {
    exportCsv(
      `reconciliation-exceptions-${new Date().toISOString().slice(0, 10)}.csv`,
      ['expense_event_id', 'trip_id', 'category', 'merchant', 'status', 'base_amount', 'split_total', 'delta', 'created_at'],
      reconciliationExceptions.map((row) => [
        row.expense_event_id,
        row.trip_id,
        row.category,
        row.merchant,
        row.aggregate_status,
        Number(row.base_amount || 0),
        Number(row.split_total || 0),
        Number(row.delta || 0),
        row.created_at,
      ]),
    );
  };

  const onCreateRule = async () => {
    try {
      setErrorMessage(null);
      const created = await createApprovalRule({
        department: newRule.department || null,
        category: newRule.category || null,
        threshold_amount: Number(newRule.threshold_amount || 0),
        requires_finance: newRule.requires_finance,
        requires_manager: true,
        auto_approve_below_threshold: true,
        sla_hours: Number(newRule.sla_hours || 24),
      });
      setRules((prev) => [created, ...prev]);
      setNewRule({ department: '', category: '', threshold_amount: 0, requires_finance: false, sla_hours: 24 });
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to create approval rule.');
    }
  };

  const onCreateDelegation = async () => {
    try {
      setErrorMessage(null);
      const start = newDelegation.start_at ? new Date(newDelegation.start_at).toISOString() : '';
      const end = newDelegation.end_at ? new Date(newDelegation.end_at).toISOString() : null;
      const created = await createApprovalDelegation({
        approver_id: newDelegation.approver_id,
        delegate_approver_id: newDelegation.delegate_approver_id,
        start_at: start,
        end_at: end,
        reason: newDelegation.reason || undefined,
      });
      setDelegations((prev) => [created, ...prev]);
      setNewDelegation({ approver_id: '', delegate_approver_id: '', start_at: '', end_at: '', reason: '' });
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to create delegation.');
    }
  };

  const onToggleDelegation = async (row: ApprovalDelegationRecord) => {
    try {
      setErrorMessage(null);
      const updated = await updateApprovalDelegation(row.id, { is_active: !row.is_active });
      setDelegations((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to update delegation.');
    }
  };

  const onToggleRule = async (rule: ApprovalRuleRecord) => {
    try {
      setErrorMessage(null);
      const updated = await updateApprovalRule(rule.id, { is_active: !rule.is_active });
      setRules((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to update approval rule.');
    }
  };

  const onRunEscalation = async () => {
    try {
      await runEscalationScan();
      const refreshed = await getOpsAlerts();
      setAlerts(refreshed);
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to run escalation scan.');
    }
  };

  const onSaveTrustConfig = async () => {
    try {
      setErrorMessage(null);
      const updated = await updateTrustConfig(trustConfig);
      setTrustConfig(updated);
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to update trust config.');
    }
  };

  const onSaveContinuityConfig = async () => {
    try {
      setErrorMessage(null);
      const updated = await updateApprovalContinuityConfig(continuityConfig);
      setContinuityConfig(updated);
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to update continuity config.');
    }
  };

  const onToggleNotificationPreference = async (
    row: NotificationPreferenceRecord,
    field: 'in_app_enabled' | 'email_enabled',
  ) => {
    try {
      setErrorMessage(null);
      const updated = await updateMyNotificationPreference({
        type: row.type,
        in_app_enabled: field === 'in_app_enabled' ? !row.in_app_enabled : row.in_app_enabled,
        email_enabled: field === 'email_enabled' ? !row.email_enabled : row.email_enabled,
      });
      setNotificationPreferences((prev) => prev.map((item) => (item.type === updated.type ? updated : item)));
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to update notification preference.');
    }
  };

  return (
    <AdminShell
      pageTitle="Settings"
      subtitle="Manage enterprise controls, admin access, and environment visibility."
    >
      {isLoading ? <LoadingDNA /> : null}
      {errorMessage ? <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{errorMessage}</p> : null}

      {!isLoading ? (
        <div className="space-y-4">
          <MetaStrip
            left="Reporting period: current control cycle"
            right={`Last refreshed: ${lastRefreshedAt ? lastRefreshedAt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}`}
          />

          <div className="grid gap-3 md:grid-cols-3">
            <KpiCard label="Total Admins" value={String(admins.length)} helper={`${activeAdmins} currently active`} />
            <KpiCard label="Approval Rules" value={String(rules.length)} helper="Policy rules in current configuration" />
            <KpiCard label="Data Footprint" value={`${totalTrips} trips`} helper={`${totalExpenses} expense events in cycle`} />
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <KpiCard label="API Requests" value={String(metrics?.requests_total || 0)} helper={`Avg latency: ${Math.round(metrics?.latency_ms.avg || 0)} ms`} />
            <KpiCard label="Reconciliation Delta" value={formatCurrencyINRFull(Number(reconciliation?.delta || 0))} helper={reconciliation?.parity_ok ? 'Parity OK' : 'Parity mismatch detected'} tone={reconciliation?.parity_ok ? 'positive' : 'danger'} />
            <KpiCard label="Active Alerts" value={String(alerts.length)} helper="Ops and escalation alerts" tone={alerts.length > 0 ? 'warning' : 'positive'} />
          </div>

          <SectionCard
            title="Policy Rules"
            subtitle="Define routing logic, thresholds, and SLA for approvals."
            actions={
              <button
                type="button"
                onClick={onRunEscalation}
                className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Run Escalation Scan
              </button>
            }
          >
            <div className="grid gap-2 md:grid-cols-5">
              <input
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700"
                placeholder="Department"
                value={newRule.department}
                onChange={(event) => setNewRule((prev) => ({ ...prev, department: event.target.value }))}
              />
              <input
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700"
                placeholder="Category"
                value={newRule.category}
                onChange={(event) => setNewRule((prev) => ({ ...prev, category: event.target.value }))}
              />
              <input
                type="number"
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700"
                placeholder="Threshold"
                value={newRule.threshold_amount}
                onChange={(event) => setNewRule((prev) => ({ ...prev, threshold_amount: Number(event.target.value || 0) }))}
              />
              <input
                type="number"
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700"
                placeholder="SLA (hrs)"
                value={newRule.sla_hours}
                onChange={(event) => setNewRule((prev) => ({ ...prev, sla_hours: Number(event.target.value || 0) }))}
              />
              <button
                type="button"
                onClick={onCreateRule}
                className="h-9 rounded-lg bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Add Rule
              </button>
            </div>

            {rules.length > 0 ? (
              <>
                <TableControls
                  searchValue={ruleSearch}
                  onSearchChange={setRuleSearch}
                  searchPlaceholder="Search rule ID, department, category, owner..."
                  filters={(
                    <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                      <select
                        value={ruleStatusFilter}
                        onChange={(event) => setRuleStatusFilter(event.target.value as 'all' | 'active' | 'inactive')}
                        className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-slate-500"
                      >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </label>
                  )}
                />

                {filteredRules.length === 0 ? (
                  <EmptyState compact message="No policy rules match the selected filters." />
                ) : (
                  <div className="overflow-x-auto">
                <table className="w-full min-w-[880px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Rule</th>
                      <th className="pb-2">Department</th>
                      <th className="pb-2">Category</th>
                      <th className="pb-2 text-right">Threshold</th>
                      <th className="pb-2">SLA</th>
                      <th className="pb-2">Flow</th>
                      <th className="pb-2">Owner</th>
                      <th className="pb-2">Last Triggered</th>
                      <th className="pb-2">Status</th>
                      <th className="pb-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRules.map((rule) => (
                      <tr key={rule.id} className="border-b border-slate-100 text-sm text-slate-700">
                        <td className="py-2.5 font-semibold text-slate-900">{rule.id.slice(0, 8)}</td>
                        <td className="py-2.5">{rule.department || 'All'}</td>
                        <td className="py-2.5">{rule.category || 'All'}</td>
                        <td className="py-2.5 text-right font-semibold text-slate-900">{formatCurrencyINRFull(Number(rule.threshold_amount || 0))}</td>
                        <td className="py-2.5">{rule.sla_hours || '-'}h</td>
                        <td className="py-2.5">{rule.requires_finance ? 'Manager + Finance' : 'Manager'}</td>
                        <td className="py-2.5">{rule.owner_name || 'Unassigned'}</td>
                        <td className="py-2.5">{rule.last_triggered_at ? formatDateIN(new Date(rule.last_triggered_at)) : '-'}</td>
                        <td className="py-2.5"><StatusBadge label={rule.is_active ? 'Active' : 'Inactive'} tone={rule.is_active ? 'positive' : 'neutral'} /></td>
                        <td className="py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => onToggleRule(rule)}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            {rule.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm font-semibold text-slate-600">No approval rules configured.</p>
            )}
          </SectionCard>

          <SectionCard
            title="Trust Layer Configuration"
            subtitle="Tune duplicate window, odd-hour bounds, and deviation threshold without code changes."
            actions={
              <button
                type="button"
                onClick={onSaveTrustConfig}
                className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
              >
                Save Trust Config
              </button>
            }
          >
            <div className="grid gap-3 md:grid-cols-4">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Duplicate Window (Hours)
                <input
                  type="number"
                  className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-sm font-semibold text-slate-700"
                  value={trustConfig.duplicate_window_hours}
                  onChange={(event) => setTrustConfig((prev) => ({ ...prev, duplicate_window_hours: Number(event.target.value || 0) }))}
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Odd Hour Start (0-23)
                <input
                  type="number"
                  className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-sm font-semibold text-slate-700"
                  value={trustConfig.odd_hour_start}
                  onChange={(event) => setTrustConfig((prev) => ({ ...prev, odd_hour_start: Number(event.target.value || 0) }))}
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Odd Hour End (0-23)
                <input
                  type="number"
                  className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-sm font-semibold text-slate-700"
                  value={trustConfig.odd_hour_end}
                  onChange={(event) => setTrustConfig((prev) => ({ ...prev, odd_hour_end: Number(event.target.value || 0) }))}
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Deviation Multiplier
                <input
                  type="number"
                  step="0.1"
                  className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-sm font-semibold text-slate-700"
                  value={trustConfig.high_deviation_multiplier}
                  onChange={(event) => setTrustConfig((prev) => ({ ...prev, high_deviation_multiplier: Number(event.target.value || 0) }))}
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Min Delta (INR)
                <input
                  type="number"
                  className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-sm font-semibold text-slate-700"
                  value={trustConfig.high_deviation_min_delta}
                  onChange={(event) => setTrustConfig((prev) => ({ ...prev, high_deviation_min_delta: Number(event.target.value || 0) }))}
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Lookback Days
                <input
                  type="number"
                  className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-sm font-semibold text-slate-700"
                  value={trustConfig.baseline_lookback_days}
                  onChange={(event) => setTrustConfig((prev) => ({ ...prev, baseline_lookback_days: Number(event.target.value || 0) }))}
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Minimum Samples
                <input
                  type="number"
                  className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-sm font-semibold text-slate-700"
                  value={trustConfig.minimum_history_samples}
                  onChange={(event) => setTrustConfig((prev) => ({ ...prev, minimum_history_samples: Number(event.target.value || 0) }))}
                />
              </label>
            </div>
          </SectionCard>

          <SectionCard
            title="Approval Continuity"
            subtitle="Set backup approver and auto-escalation window for overdue manager approvals."
            actions={
              <button
                type="button"
                onClick={onSaveContinuityConfig}
                className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
              >
                Save Continuity Config
              </button>
            }
          >
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Backup Approver
                <select
                  className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-sm font-semibold text-slate-700"
                  value={continuityConfig.backup_approver_id || ''}
                  onChange={(event) => setContinuityConfig((prev) => ({ ...prev, backup_approver_id: event.target.value || null }))}
                >
                  <option value="">No backup (fallback to finance/admin)</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name || employee.email} {employee.role ? `(${employee.role})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Auto Escalation (Hours)
                <input
                  type="number"
                  min={1}
                  className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-sm font-semibold text-slate-700"
                  value={continuityConfig.auto_escalation_hours}
                  onChange={(event) => setContinuityConfig((prev) => ({ ...prev, auto_escalation_hours: Number(event.target.value || 1) }))}
                />
              </label>
            </div>
          </SectionCard>

          <SectionCard
            title="Approval Delegation (Out of Office)"
            subtitle="Route manager approvals to delegates during absence windows."
          >
            <div className="grid gap-2 md:grid-cols-5">
              <select
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700"
                value={newDelegation.approver_id}
                onChange={(event) => setNewDelegation((prev) => ({ ...prev, approver_id: event.target.value }))}
              >
                <option value="">Select approver</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name || employee.email} {employee.role ? `(${employee.role})` : ''}
                  </option>
                ))}
              </select>
              <select
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700"
                value={newDelegation.delegate_approver_id}
                onChange={(event) => setNewDelegation((prev) => ({ ...prev, delegate_approver_id: event.target.value }))}
              >
                <option value="">Select delegate</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name || employee.email} {employee.role ? `(${employee.role})` : ''}
                  </option>
                ))}
              </select>
              <input
                type="datetime-local"
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700"
                value={newDelegation.start_at}
                onChange={(event) => setNewDelegation((prev) => ({ ...prev, start_at: event.target.value }))}
              />
              <input
                type="datetime-local"
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700"
                value={newDelegation.end_at}
                onChange={(event) => setNewDelegation((prev) => ({ ...prev, end_at: event.target.value }))}
              />
              <button
                type="button"
                onClick={onCreateDelegation}
                className="h-9 rounded-lg bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Add Delegation
              </button>
            </div>
            <input
              className="mt-2 h-9 w-full rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700"
              placeholder="Reason (optional)"
              value={newDelegation.reason}
              onChange={(event) => setNewDelegation((prev) => ({ ...prev, reason: event.target.value }))}
            />

            {delegations.length > 0 ? (
              <>
                <TableControls
                  searchValue={delegationSearch}
                  onSearchChange={setDelegationSearch}
                  searchPlaceholder="Search approver, delegate, reason..."
                  filters={(
                    <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                      <select
                        value={delegationStatusFilter}
                        onChange={(event) => setDelegationStatusFilter(event.target.value as 'all' | 'active' | 'inactive')}
                        className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-slate-500"
                      >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </label>
                  )}
                />
                {filteredDelegations.length === 0 ? (
                  <EmptyState compact message="No delegations match the selected filters." />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[980px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <th className="pb-2">Delegation</th>
                          <th className="pb-2">Approver</th>
                          <th className="pb-2">Delegate</th>
                          <th className="pb-2">Start</th>
                          <th className="pb-2">End</th>
                          <th className="pb-2">Reason</th>
                          <th className="pb-2">Status</th>
                          <th className="pb-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDelegations.map((row) => (
                          <tr key={row.id} className="border-b border-slate-100 text-sm text-slate-700">
                            <td className="py-2.5 font-semibold text-slate-900">{row.id.slice(0, 8)}</td>
                            <td className="py-2.5">{row.approver_name || row.approver_id}</td>
                            <td className="py-2.5">{row.delegate_approver_name || row.delegate_approver_id}</td>
                            <td className="py-2.5">{formatDateIN(row.start_at ? new Date(row.start_at) : null)}</td>
                            <td className="py-2.5">{formatDateIN(row.end_at ? new Date(row.end_at) : null)}</td>
                            <td className="py-2.5">{row.reason || '-'}</td>
                            <td className="py-2.5">
                              <StatusBadge label={row.is_active ? 'Active' : 'Inactive'} tone={row.is_active ? 'positive' : 'neutral'} />
                            </td>
                            <td className="py-2.5 text-right">
                              <button
                                type="button"
                                onClick={() => onToggleDelegation(row)}
                                className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                              >
                                {row.is_active ? 'Disable' : 'Enable'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm font-semibold text-slate-600">No delegation rules configured.</p>
            )}
          </SectionCard>

          <SectionCard
            title="Notification Preferences"
            subtitle="Control in-app and email delivery for each automation trigger."
          >
            {notificationPreferences.length === 0 ? (
              <p className="text-sm font-semibold text-slate-600">No notification preferences found. Defaults are enabled for all channels.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Trigger Type</th>
                      <th className="pb-2">In-App</th>
                      <th className="pb-2">Email</th>
                      <th className="pb-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notificationPreferences.map((row) => (
                      <tr key={row.type} className="border-b border-slate-100 text-sm text-slate-700">
                        <td className="py-2.5 font-semibold text-slate-900">{row.type.replace(/_/g, ' ')}</td>
                        <td className="py-2.5">
                          <StatusBadge label={row.in_app_enabled ? 'Enabled' : 'Disabled'} tone={row.in_app_enabled ? 'positive' : 'neutral'} />
                        </td>
                        <td className="py-2.5">
                          <StatusBadge label={row.email_enabled ? 'Enabled' : 'Disabled'} tone={row.email_enabled ? 'positive' : 'neutral'} />
                        </td>
                        <td className="py-2.5 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => onToggleNotificationPreference(row, 'in_app_enabled')}
                              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Toggle In-App
                            </button>
                            <button
                              type="button"
                              onClick={() => onToggleNotificationPreference(row, 'email_enabled')}
                              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Toggle Email
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Operational Alerts" subtitle="System alerts from escalation and monitoring checks.">
            {alerts.length === 0 ? (
              <p className="text-sm font-semibold text-slate-600">No active alerts.</p>
            ) : (
              <>
                <TableControls
                  searchValue={alertSearch}
                  onSearchChange={setAlertSearch}
                  searchPlaceholder="Search alert code, severity, message..."
                />
                {filteredAlerts.length === 0 ? (
                  <EmptyState compact message="No alerts match the selected filters." />
                ) : (
                  <ul className="space-y-2">
                    {filteredAlerts.map((alert) => (
                      <li key={alert.code} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        <span className="mr-2 font-semibold text-slate-900">{alert.code}</span>
                        <StatusBadge
                          label={String(alert.severity || 'info').toUpperCase()}
                          tone={String(alert.severity || '').toLowerCase() === 'critical' ? 'danger' : 'warning'}
                        />
                        <span className="ml-2">{alert.message}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </SectionCard>

          <SectionCard
            title="Reconciliation Exceptions"
            subtitle="Expense events where split totals do not match base amount."
            actions={
              reconciliationExceptions.length > 0 ? (
                <button
                  type="button"
                  onClick={onExportReconciliationExceptions}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Export Exception Pack
                </button>
              ) : null
            }
          >
            {reconciliationExceptions.length === 0 ? (
              <p className="text-sm font-semibold text-slate-600">No reconciliation exceptions in the current cycle.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Expense Event</th>
                      <th className="pb-2">Trip</th>
                      <th className="pb-2">Category</th>
                      <th className="pb-2">Merchant</th>
                      <th className="pb-2 text-right">Base Amount</th>
                      <th className="pb-2 text-right">Split Total</th>
                      <th className="pb-2 text-right">Delta</th>
                      <th className="pb-2">Status</th>
                      <th className="pb-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconciliationExceptions.slice(0, 30).map((row) => (
                      <tr key={row.expense_event_id} className="border-b border-slate-100 text-sm text-slate-700">
                        <td className="py-2.5 font-semibold text-slate-900">{row.expense_event_id.slice(0, 8)}</td>
                        <td className="py-2.5">{row.trip_id ? row.trip_id.slice(0, 8) : '-'}</td>
                        <td className="py-2.5">{row.category || '-'}</td>
                        <td className="py-2.5">{row.merchant || '-'}</td>
                        <td className="py-2.5 text-right">{formatCurrencyINRFull(Number(row.base_amount || 0))}</td>
                        <td className="py-2.5 text-right">{formatCurrencyINRFull(Number(row.split_total || 0))}</td>
                        <td className="py-2.5 text-right font-semibold text-red-700">{formatCurrencyINRFull(Number(row.delta || 0))}</td>
                        <td className="py-2.5">{row.aggregate_status || '-'}</td>
                        <td className="py-2.5">{formatDateIN(row.created_at ? new Date(row.created_at) : null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Admin Access Directory" subtitle="Current admin roster and account state.">
            {admins.length > 0 ? (
              <>
                <TableControls
                  searchValue={adminSearch}
                  onSearchChange={setAdminSearch}
                  searchPlaceholder="Search admin ID, email, company ID, user ID..."
                  filters={(
                    <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                      <select
                        value={adminStatusFilter}
                        onChange={(event) => setAdminStatusFilter(event.target.value as 'all' | 'active' | 'inactive')}
                        className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-slate-500"
                      >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </label>
                  )}
                />

                {filteredAdmins.length === 0 ? (
                  <EmptyState compact message="No admins match the selected filters." />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[880px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <th className="pb-2">Admin ID</th>
                          <th className="pb-2">Email</th>
                          <th className="pb-2">Company ID</th>
                          <th className="pb-2">User ID</th>
                          <th className="pb-2">Status</th>
                          <th className="pb-2">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAdmins.map((admin) => (
                          <tr key={admin.id} className="border-b border-slate-100 text-sm text-slate-700">
                            <td className="py-3 font-semibold text-slate-900">{admin.id.slice(0, 8)}</td>
                            <td className="py-3">{admin.email}</td>
                            <td className="py-3">{admin.company_id.slice(0, 8)}</td>
                            <td className="py-3">{admin.user_id.slice(0, 8)}</td>
                            <td className="py-3">
                              <StatusBadge label={admin.is_active ? 'Active' : 'Inactive'} tone={admin.is_active ? 'positive' : 'neutral'} />
                            </td>
                            <td className="py-3">{formatDateIN(admin.created_at ? new Date(admin.created_at) : null)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm font-semibold text-slate-600">No admins found for this company.</p>
            )}
          </SectionCard>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminSettingsPage;
